/**
 * Session Manager - Handles saving and loading conversation sessions
 */

export interface SessionMetadata {
  session_id: string;
  title: string;
  agent: string;
  created_at: number;
  last_accessed_at: number;
  message_count: number;
}

export interface StoredSession extends SessionMetadata {
  messages: Array<{
    sender: string;
    content: string;
    chips?: any[];
    image_path?: string;
    attachments?: any[];
    folder_info?: {
      name: string;
      file_count: number;
    };
  }>;
}

const SESSION_PREFIX = "chat_session_";
const SESSIONS_INDEX_KEY = "chat_sessions_index";

/**
 * Get all session metadata for a specific user
 */
export const getAllSessions = (userId: string): SessionMetadata[] => {
  if (!userId) return [];
  try {
    const index = localStorage.getItem(`${SESSIONS_INDEX_KEY}${userId}`);
    if (!index) return [];
    return JSON.parse(index) as SessionMetadata[];
  } catch (error) {
    console.warn("Failed to load sessions index", error);
    return [];
  }
};

/**
 * Get a specific session with messages for a specific user
 */
export const getSession = (userId: string, sessionId: string): StoredSession | null => {
  if (!userId || !sessionId) return null;
  try {
    const data = localStorage.getItem(`${SESSION_PREFIX}${userId}_${sessionId}`);
    if (!data) return null;
    return JSON.parse(data) as StoredSession;
  } catch (error) {
    console.warn(`Failed to load session ${sessionId}`, error);
    return null;
  }
};

/**
 * Save a session with its messages for a specific user
 */
export const saveSession = (
  userId: string,
  sessionId: string,
  agent: string | null,
  messages: Array<{
    sender: string;
    content: string;
    chips?: any[];
    image_path?: string;
    attachments?: any[];
    folder_info?: {
      name: string;
      file_count: number;
    };
  }>,
  title?: string
): void => {
  try {
    if (!userId || !sessionId || !agent) return;

    // Generate title from first message if not provided
    let finalTitle = title;
    if (!finalTitle && messages.length > 0) {
      const firstMessage = messages.find((m) => m.sender === "You");
      if (firstMessage) {
        finalTitle = firstMessage.content.substring(0, 50).trim();
        if (firstMessage.content.length > 50) {
          finalTitle += "...";
        }
      }
    }
    finalTitle = finalTitle || `Chat - ${new Date().toLocaleDateString()}`;

    const now = Date.now();
    const session: StoredSession = {
      session_id: sessionId,
      title: finalTitle,
      agent,
      created_at: now,
      last_accessed_at: now,
      message_count: messages.length,
      messages,
    };

    // Save session data with user-specific key
    localStorage.setItem(`${SESSION_PREFIX}${userId}_${sessionId}`, JSON.stringify(session));

    // Update index
    let sessions = getAllSessions(userId);
    const existingIndex = sessions.findIndex((s) => s.session_id === sessionId);

    if (existingIndex >= 0) {
      sessions[existingIndex] = {
        session_id: sessionId,
        title: finalTitle,
        agent,
        created_at: sessions[existingIndex].created_at,
        last_accessed_at: now,
        message_count: messages.length,
      };
    } else {
      sessions.unshift({
        session_id: sessionId,
        title: finalTitle,
        agent,
        created_at: now,
        last_accessed_at: now,
        message_count: messages.length,
      });
    }

    // Sort by last_accessed_at descending
    sessions.sort((a, b) => b.last_accessed_at - a.last_accessed_at);

    // Limit to 5 sessions and clean up removed ones
    if (sessions.length > 5) {
      const toRemove = sessions.slice(5);
      toRemove.forEach(s => {
        localStorage.removeItem(`${SESSION_PREFIX}${userId}_${s.session_id}`);
      });
      sessions = sessions.slice(0, 5);
    }

    localStorage.setItem(`${SESSIONS_INDEX_KEY}${userId}`, JSON.stringify(sessions));
  } catch (error) {
    console.warn("Failed to save session", error);
  }
};

/**
 * Delete a session for a specific user
 */
export const deleteSession = (userId: string, sessionId: string): void => {
  if (!userId || !sessionId) return;
  try {
    localStorage.removeItem(`${SESSION_PREFIX}${userId}_${sessionId}`);
    const sessions = getAllSessions(userId);
    const filtered = sessions.filter((s) => s.session_id !== sessionId);
    localStorage.setItem(`${SESSIONS_INDEX_KEY}${userId}`, JSON.stringify(filtered));
  } catch (error) {
    console.warn("Failed to delete session", error);
  }
};

/**
 * Update session title for a specific user
 */
export const updateSessionTitle = (userId: string, sessionId: string, title: string): void => {
  if (!userId || !sessionId) return;
  try {
    const session = getSession(userId, sessionId);
    if (!session) return;

    session.title = title;
    session.last_accessed_at = Date.now();
    localStorage.setItem(`${SESSION_PREFIX}${userId}_${sessionId}`, JSON.stringify(session));

    const sessions = getAllSessions(userId);
    const existingIndex = sessions.findIndex((s) => s.session_id === sessionId);
    if (existingIndex >= 0) {
      sessions[existingIndex].title = title;
      sessions[existingIndex].last_accessed_at = Date.now();
      localStorage.setItem(`${SESSIONS_INDEX_KEY}${userId}`, JSON.stringify(sessions));
    }
  } catch (error) {
    console.warn("Failed to update session title", error);
  }
};